// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import { ButtonPresentationProps } from "@insite/mobius/Button";
import { IconWrapper } from "@insite/mobius/Icon";
import { LinkPresentationProps } from "@insite/mobius/Link";
import { TypographyProps } from "@insite/mobius/Typography";
import TypographyStyle from "@insite/mobius/Typography/TypographyStyle";
import { getProp } from "@insite/mobius/utilities";
import { buttonTransparent } from "@pace/XDL/PreStyle/button";
import { fontConfig } from "@pace/XDL/PreStyle/typography";
import color from "@pace/XDL/Utilities/color";
import * as React from "react";
import { css } from "styled-components";

export const linkArrowStyle: LinkPresentationProps = {
    icon: {
        iconProps: {
            // src: ArrowRight,
            size: 18,
            color: "primary",
            css: css`
                opacity: 0.33;
                margin-left: 8px;
            `,
        },
        position: "right",
    },
    typographyProps: {
        size: 14,
        ellipsis: true,
        weight: "bold",
        css: css`
            letter-spacing: 0;
            line-height: 16px;
        `,
    },
    css: css`
        justify-content: left;
        // prevent take full width
        align-self: flex-start;

        &:hover {
            & > ${IconWrapper}, & > ${TypographyStyle} {
                color: ${color("common.ui8")};
            }
        }
    `,
};

interface IconButtonProps {
    typographyProps?: TypographyProps;
}

export const iconButtonCommonStyles = (
    iconSrc: React.ComponentType | string,
    iconSize?: number,
    buttonProps?: IconButtonProps,
    iconColor?: string,
) => {
    return {
        mergeCss: true,
        icon: {
            src: iconSrc,
            position: "left",
        },
        typographyProps: {
            color: "secondary",
            size: 12,
            weight: fontConfig.weight.bold,
            transform: "capitalize",
            ...buttonProps?.typographyProps,
            css: css`
                letter-spacing: 0;
                line-height: 13px;
                ${buttonProps?.typographyProps?.css}
            `,
        },
        css: css`
            padding: 0;
            ${buttonTransparent}

            ${IconWrapper} {
                margin-right: 8px;
                opacity: 0.5;
                color: ${iconColor ? color(iconColor) : color("primary")};
                &,
                & svg {
                    width: ${iconSize || 13}px;
                    height: auto;
                }
            }

            &:hover:not([disabled]),
            &:focus:hover {
                & ${TypographyStyle} {
                    color: ${color("common.ui8")};
                }
                & ${IconWrapper} {
                    color: ${color("common.ui8")};
                    opacity: 1;
                }
            }
        `,
    } as ButtonPresentationProps;
};

export const iconLinkCommonStyles = (iconSrc: React.ComponentType | string) => {
    return {
        color: "primary",
        icon: {
            iconProps: {
                src: iconSrc,
                size: 13,
                color: "primary",
                css: css`
                    margin-right: 8px;
                    opacity: 0.5;
                `,
            },
            position: "left",
        },
        typographyProps: {
            size: 12,
            weight: fontConfig.weight.bold,
            css: css`
                letter-spacing: 0;
                line-height: 13px;
            `,
        },
        css: css`
            transition: all ${getProp("theme.transition.duration.regular")}ms ease-in-out;
            &:disabled {
                &,
                & ${TypographyStyle}, & ${IconWrapper} {
                    color: ${color("primary", 0.5)};
                }
            }
            &:hover {
                &,
                & > ${TypographyStyle}, & ${IconWrapper} {
                    color: ${color("common.ui3")};
                }
                & ${IconWrapper} {
                    opacity: 1;
                }
            }
        `,
    } as LinkPresentationProps;
};
